// The checker: isOfficial(address) and describe(address) on the mainnet verifier, plus the token's beacon
// slot and implementation as evidence. A one-word answer is required for the verdict; "0x" from a
// throttled node is reported as "could not check", never as false.
import { decodeDescribe, encAddr, hexToBig, wordAddr, wordBig, type Describe } from './abi';
import { BEACON_SLOT, ISSUER_IMPL, MAINNET, NVDA, SEL, VERIFIER } from './chain';
import { readFeed, type FeedReading } from './feed';
import { callWord, callWords, rpc } from './rpc';
import { feedFor, type Snapshot } from './snapshot';

export interface KnownFeed { sym: string; feed: string; reading: FeedReading | null }

export type CheckResult =
  | { kind: 'no-code' }
  | {
      kind: 'verdict';
      official: boolean;
      describe: Describe | null;
      /** Raw storage word of the beacon slot, or null when it could not be read. */
      slot: string | null;
      beacon: string | null;
      impl: string | null;
      implIsIssuer: boolean;
      known: KnownFeed | null;
      /** Reasons the evidence shows for a failed check. */
      missing: string[];
    };

export async function readNvda(): Promise<Describe> {
  const raw = await callWords(VERIFIER, SEL.describe + encAddr(NVDA), 7, MAINNET);
  return decodeDescribe(raw);
}

export async function checkToken(addr: string, snap: Snapshot | null): Promise<CheckResult> {
  const code = await rpc('eth_getCode', [addr, 'latest']);
  if (typeof code !== 'string' || !code.startsWith('0x')) throw new Error('malformed eth_getCode answer');
  if (code === '0x') return { kind: 'no-code' };

  const officialRaw = await callWord(VERIFIER, SEL.isOfficial + encAddr(addr));
  const official = wordBig(officialRaw.slice(2), 0) === 1n;

  const [descRaw, slotRaw] = await Promise.all([
    callWords(VERIFIER, SEL.describe + encAddr(addr), 7).catch(() => null),
    rpc('eth_getStorageAt', [addr, BEACON_SLOT, 'latest']).catch(() => null),
  ]);
  let describe: Describe | null = null;
  try { describe = descRaw ? decodeDescribe(descRaw) : null; } catch { describe = null; }
  const slot = typeof slotRaw === 'string' ? slotRaw : null;
  const beacon = slot && /^0x[0-9a-fA-F]{64}$/.test(slot) && hexToBig(slot) !== 0n ? '0x' + slot.slice(-40) : null;
  let impl: string | null = null;
  if (beacon) {
    try { impl = wordAddr((await callWord(beacon, SEL.implementation)).slice(2), 0); } catch { impl = null; }
  }
  const implIsIssuer = impl !== null && impl.toLowerCase() === ISSUER_IMPL;

  const k = feedFor(snap, addr);
  let known: KnownFeed | null = null;
  if (k) {
    let reading: FeedReading | null = null;
    try { reading = await readFeed(k.feed, MAINNET); } catch { reading = null; }
    known = { ...k, reading };
  }

  const missing: string[] = [];
  if (describe) {
    if (hexToBig(describe.uid) === 0n) missing.push('the uid is zero');
    if (!describe.symbol) missing.push('the symbol is empty');
  }
  if (impl && !implIsIssuer) missing.push("the implementation is not the issuer's Stock contract");
  if (slot && !beacon) missing.push('the address is not a beacon proxy');

  return { kind: 'verdict', official, describe, slot, beacon, impl, implIsIssuer, known, missing };
}
